import {
    CoachAssignmentResult,
    SessionDetailAssignedCoach
} from "../services/types/sessionDetailTypes";

/** True only when the assign endpoint reported results and every requested change was stored. */
export const allCoachAssignmentsStored = (
    results: CoachAssignmentResult[] | undefined | null,
    expectedCount: number
): boolean =>
    results != null &&
    results.length === expectedCount &&
    expectedCount > 0 &&
    results.every((r) => r.success);

/**
 * Returns a user-facing failure detail when the assign endpoint reports that any requested change
 * was not stored (or reports nothing at all), otherwise null. The API answers with per-item results,
 * either in a 200 body or, when something failed, in the body of a 400 response.
 */
export const getCoachAssignmentFailureMessage = (
    results: CoachAssignmentResult[] | undefined | null,
    expectedCount: number
): string | null => {
    if (allCoachAssignmentsStored(results, expectedCount)) return null;

    if (results == null || results.length === 0)
        return "No results were returned, so the changes may not have been saved.";

    if (results.length !== expectedCount)
        return `The response included ${results.length} of ${expectedCount} expected results.`;

    const failed = results.filter((r) => !r.success);
    const firstReason = failed.find((r) => r.errMessage)?.errMessage;
    const summary = `${failed.length} of ${results.length} changes could not be saved.`;
    return firstReason ? `${summary} ${firstReason}` : summary;
};

/** Pulls the per-item results out of an RTK Query error, if the API sent them. */
export const getCoachAssignmentResultsFromError = (
    error: unknown
): CoachAssignmentResult[] | null => {
    const results = (error as { data?: { coachAssignmentResponses?: CoachAssignmentResult[] } })
        ?.data?.coachAssignmentResponses;
    return Array.isArray(results) ? results : null;
};

/**
 * After a partial save, applies the changes the API did store to the stored baseline, so that later
 * edits are diffed against what is really on the server. Without it, removing an assignment that was
 * stored by the partial save would produce no removal request, leaving it on the server.
 * A stored result is an addition when the working list holds that coach/participant pair, otherwise
 * a removal. Adding a primary coach also replaces that participant's previous primary, as the API does.
 */
export const applyStoredCoachAssignments = (
    stored: SessionDetailAssignedCoach[],
    working: SessionDetailAssignedCoach[],
    results: CoachAssignmentResult[]
): SessionDetailAssignedCoach[] => {
    let next = [...stored];
    results
        .filter((r) => r.success && r.coachAssignment != null)
        .forEach((r) => {
            const { imKey, paxImKey, isPrimary } = r.coachAssignment;
            const isSameAssignment = (a: SessionDetailAssignedCoach) =>
                a.imKey === imKey && a.paxImKey === paxImKey && a.isPrimary === isPrimary;

            if (r.isRemove) {
                next = next.filter((a) => !isSameAssignment(a));
                return;
            }

            const workingItem = working.find(isSameAssignment);
            if (workingItem == null) return;
            if (workingItem.isPrimary)
                next = next.filter(
                    (a) => !(a.paxImKey === paxImKey && a.isPrimary && a.imKey !== imKey)
                );
            if (!next.some(isSameAssignment)) next.push(workingItem);
        });
    return next;
};
